import { z } from 'zod';
import { InterruptPromptSchema } from './chat.js';
// ============================================================================
// Public (외부 임베드용) 스키마
// ============================================================================
export const AgentStarterPromptSchema = z.object({
    label: z.string().min(1).max(120),
    prompt: z.string().min(1).max(2000),
});
export const GetAgentInfoResponseSchema = z.object({
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    starterPrompts: z.array(AgentStarterPromptSchema),
    isEnabled: z.boolean(),
});
export const CreateAgentSessionRequestSchema = z.object({
    /** 외부 사용자 식별자 (예: empno). audit / 격리 / rate-limit 키. */
    externalUserRef: z.string().min(1).max(200),
    /** 호스트 컨텍스트 (예: 선택된 마케터/팀/PT). innermate 가 system 메시지 주입에 사용 가능. */
    scope: z.record(z.unknown()).optional(),
    /** 사용자가 보는 첫 제목 (선택). 미지정 시 첫 메시지에서 자동 생성. */
    title: z.string().max(200).optional(),
});
export const CreateAgentSessionResponseSchema = z.object({
    sessionId: z.string().uuid(),
});
export const AgentSessionSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * 세션 검색 / 페이지네이션 파라미터. 쿼리스트링에서 들어오므로 number 는 coerce.
 * - `q`: 제목 부분 문자열 (ILIKE, 대소문자 무시). 비어있으면 무필터.
 * - `limit`: 1~200, 기본 100.
 */
export const ListAgentSessionsRequestSchema = z.object({
    q: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
export const ListAgentSessionsResponseSchema = z.object({
    sessions: z.array(AgentSessionSummarySchema),
});
export const SendAgentMessageRequestSchema = z.object({
    content: z.string().min(1).max(20_000),
});
export const RenameAgentSessionRequestSchema = z.object({
    title: z.string().min(1).max(200),
});
// ----- 외부 SSE 이벤트 -----
export const AgentStreamPhaseSchema = z.enum(['thinking', 'searching', 'analyzing', 'fetching']);
export const AgentStreamEventSchema = z.discriminatedUnion('t', [
    z.object({ t: z.literal('delta'), v: z.string() }),
    z.object({ t: z.literal('status'), phase: AgentStreamPhaseSchema }),
    z.object({
        t: z.literal('done'),
        messageId: z.string().uuid(),
        responseTimeMs: z.number().int().nonnegative().optional(),
    }),
    z.object({ t: z.literal('error'), message: z.string() }),
    /**
     * 모델이 비정상 출력(무한 반복/끝없는 생성)을 내 스트림을 중단하고 다시 생성할 때 emit.
     * 클라이언트는 지금까지 받은 진행 중 assistant 텍스트를 즉시 비우고 이어지는 새 delta 로 대체한다.
     */
    z.object({ t: z.literal('reset') }),
    /**
     * 경고 — 스트림은 정상 종료되지만 한도 초과 / 도구 연속 실패 등으로 답변이 잘렸음을 알림.
     * 외부 agent 도 사용자에게 사유를 보여줄 수 있도록 forward. ChatStreamEvent.warning 과 동일.
     * 클라이언트는 done 직전에 토스트/배너로 안내 권장.
     */
    z.object({ t: z.literal('warning'), message: z.string() }),
    /**
     * HITL — LLM 이 system.ask_user / system.batch_ask_user 호출 시 emit.
     * 클라이언트는 InterruptPrompt 필드를 모달/폼으로 렌더하고
     * POST /api/agents/:slug/sessions/:sid/interrupts/:interruptId/resolve 로 응답한다.
     * InterruptPrompt 의 모든 필드는 user-facing (LLM 이 런타임에 작성한 prompt/choices) 이라
     * 외부 노출 시 추가 sanitize 불필요.
     */
    z
        .object({
        t: z.literal('await_user_input'),
        interruptId: z.string().uuid(),
    })
        .merge(InterruptPromptSchema),
    /** 인터럽트 해소됨 (resolve / skip / 다른 follower 가 처리). 모달 닫기 신호. */
    z.object({ t: z.literal('interrupt_resolved'), interruptId: z.string().uuid() }),
]);
// ============================================================================
// Admin (어드민 CRUD) 스키마
// ============================================================================
export const AgentToolVisibilitySchema = z.enum(['hidden', 'status_only']);
export const AgentSummarySchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    /** backing skill UUID 들. 머지된 system_prompt + tool 합집합으로 동작. 최소 1개. */
    skillIds: z.array(z.string().uuid()).min(1),
    /** UI 표시용 skill 이름 배열. skillIds 와 동일 순서. */
    skillNames: z.array(z.string()).optional(),
    isEnabled: z.boolean(),
    starterPrompts: z.array(AgentStarterPromptSchema),
    toolVisibility: AgentToolVisibilitySchema,
    rateLimitPerMin: z.number().int().nonnegative(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ListAdminAgentsResponseSchema = z.object({
    agents: z.array(AgentSummarySchema),
});
const slugRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
export const CreateAdminAgentRequestSchema = z.object({
    slug: z.string().min(2).max(64).regex(slugRegex, 'slug 은 영소문자/숫자/하이픈만'),
    name: z.string().min(1).max(120),
    description: z.string().max(2000).optional().nullable(),
    /** 1~4 개. 2 개 이상이면 skill-set.ts 가 cross-domain merge 모드로 동작. */
    skillIds: z.array(z.string().uuid()).min(1).max(4),
    isEnabled: z.boolean().default(true),
    starterPrompts: z.array(AgentStarterPromptSchema).max(10).default([]),
    toolVisibility: AgentToolVisibilitySchema.default('status_only'),
    rateLimitPerMin: z.number().int().min(0).max(10_000).default(20),
});
export const UpdateAdminAgentRequestSchema = CreateAdminAgentRequestSchema.partial().extend({
// slug 변경은 외부 통합 깨질 위험 — 별도 action 으로 분리하거나 어드민 UI 에서 막음.
// 여기서는 partial 로 두되 라우트에서 거부.
});
export const AgentApiKeyRowSchema = z.object({
    id: z.string().uuid(),
    agentId: z.string().uuid(),
    label: z.string(),
    keyPrefix: z.string(),
    lastUsedAt: z.string().nullable(),
    createdAt: z.string(),
    revokedAt: z.string().nullable(),
});
export const ListAgentApiKeysResponseSchema = z.object({
    keys: z.array(AgentApiKeyRowSchema),
});
export const CreateAgentApiKeyRequestSchema = z.object({
    label: z.string().min(1).max(120),
});
export const CreateAgentApiKeyResponseSchema = z.object({
    /** 평문 — 발급 직후 한 번만 반환. 이후 hash 만 보관. */
    apiKey: z.string(),
    row: AgentApiKeyRowSchema,
});
//# sourceMappingURL=agent.js.map